// Client-side throttle for auth attempts.
//
// Hard cap: 5 attempts per 15-minute rolling window, per (action, email).
// This is a UX-layer guardrail — Firebase Auth has its own server-side rate
// limiting and App Check provides another layer. The goal here is to catch
// casual rapid-fire attempts (script kiddies, accidental double-clicks) and
// give the legitimate user a clear "wait X minutes" message instead of an
// opaque Firebase error.
//
// Failed attempts are counted; a successful call resets the counter for that
// email so legit users aren't locked out of their own account by past typos.

const STORAGE_PREFIX = "seshly_auth_throttle:";
const MAX_ATTEMPTS = 5;
const WINDOW_MS = 15 * 60 * 1000; // 15 minutes

function key(action, identifier) {
  const id = String(identifier || "anon").toLowerCase().trim();
  return `${STORAGE_PREFIX}${action}:${id}`;
}

function readAttempts(action, identifier) {
  try {
    const raw = localStorage.getItem(key(action, identifier));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    return arr.filter((t) => typeof t === "number" && now - t < WINDOW_MS);
  } catch {
    return [];
  }
}

function writeAttempts(action, identifier, arr) {
  try {
    localStorage.setItem(key(action, identifier), JSON.stringify(arr));
  } catch {
    // localStorage may be disabled (private mode, etc.) — fail open.
  }
}

/**
 * Check whether the caller is allowed to attempt this action right now.
 * If blocked, returns { allowed: false, retryInSec: number }.
 */
export function checkAuthThrottle(action, identifier) {
  const attempts = readAttempts(action, identifier);
  if (attempts.length < MAX_ATTEMPTS) {
    return { allowed: true };
  }
  const oldest = Math.min(...attempts);
  const retryInMs = Math.max(0, WINDOW_MS - (Date.now() - oldest));
  return {
    allowed: false,
    retryInSec: Math.ceil(retryInMs / 1000),
    retryInMin: Math.ceil(retryInMs / 60000),
  };
}

/** Throw if blocked; otherwise no-op. */
export function assertAuthThrottle(action, identifier) {
  const res = checkAuthThrottle(action, identifier);
  if (!res.allowed) {
    const err = new Error("AUTH_THROTTLED");
    err.code = "auth/too-many-attempts";
    err.retryInMin = res.retryInMin;
    err.retryInSec = res.retryInSec;
    err.message = `Too many attempts. Try again in ${res.retryInMin} minute${res.retryInMin === 1 ? "" : "s"}.`;
    throw err;
  }
}

/** Record one failed attempt against the (action, identifier) bucket. */
export function recordAuthFailure(action, identifier) {
  const attempts = readAttempts(action, identifier);
  attempts.push(Date.now());
  writeAttempts(action, identifier, attempts);
}

/** Clear the bucket after a successful auth flow. */
export function clearAuthThrottle(action, identifier) {
  try {
    localStorage.removeItem(key(action, identifier));
  } catch {}
}

export const AUTH_THROTTLE_LIMITS = { MAX_ATTEMPTS, WINDOW_MS };
